import React, { useEffect, useState } from "react";
import {
  Steps,
  Button,
  Input,
  Select,
  Upload,
  DatePicker,
  Typography,
  Card,
  message,
  Form,
  Modal,
  Row,
  Col,
} from "antd";
import {
  CarTwoTone,
  IdcardTwoTone,
  FileImageTwoTone,
  UploadOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import colors from "../../assets/styles/colour";
import icon from "../assets/images/register.png";
import Title from "antd/es/typography/Title";
import dataService from "../../networks/api/service/dataService";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;

const steps = [
  { title: "Vehicle Details", icon: <CarTwoTone twoToneColor="#1890ff" /> },
  {
    title: "Assign A Driver",
    icon: <FileImageTwoTone twoToneColor="#eb2f96" />,
  },
];

const TransportDetails = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [driverList, setDriverList] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
const [pendingDriver, setPendingDriver] = useState(null);
const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [formData, setFormData] = useState({
    vehicleDetails: "",
    makeAndModel: "",
    registrationNum: "",
    insuranceNumber: "",
    insuranceProvider: "",
    insuranceValidTill: null,
    lastValidityDate: null,
    serviceReminder: "",
  });
  const location = useLocation();
  const editMode = location.state?.mode === "edit";
  const vehicleToEdit = location.state?.vehicleData;
  const navigate = useNavigate();

  const handleNext = () => setCurrentStep((prevStep) => prevStep + 1);
  const handleBack = () => setCurrentStep((prevStep) => prevStep - 1);
  const handleFileUpload = (info, field) => {
    if (info.file.status === "done") {
      setFormData({ ...formData, [field]: info.file.originFileObj });
    }
  };

  useEffect(() => {
    if (editMode && vehicleToEdit) {
      setFormData({
        vehicleDetails: vehicleToEdit.vehicleDetails || "",
        makeAndModel: vehicleToEdit.makeAndModel || "",
        registrationNum: vehicleToEdit.registrationNum || "",
        insuranceNumber: vehicleToEdit.insuranceNumber || "",
        insuranceProvider: vehicleToEdit.insuranceProvider || "",
        insuranceValidTill: vehicleToEdit.insuranceValidTill
          ? dayjs(vehicleToEdit.insuranceValidTill)
          : null,
        lastValidityDate: vehicleToEdit.lastValidityDate
          ? dayjs(vehicleToEdit.lastValidityDate)
          : null,
        serviceReminder: vehicleToEdit.serviceReminder || "",
      });
    }
  }, [editMode, vehicleToEdit]);

  const handleSubmit = async () => {
    const payload = {
      vehicleDetails: formData.vehicleDetails,
      makeAndModel: formData.makeAndModel,
      registrationNum: formData.registrationNum,
      insuranceNumber: formData.insuranceNumber,
      insuranceProvider: formData.insuranceProvider,
      insuranceValidTill:
        formData.insuranceValidTill?.format("YYYY-MM-DD") || "",
      lastValidityDate: formData.lastValidityDate?.format("YYYY-MM-DD") || "",
      serviceReminder: formData.serviceReminder,
      assignedDriver: formData.assignedDriver,
    };

    try {
      let response;

      if (editMode) {
        response = await dataService.editVehicle(vehicleToEdit._id, payload);
        if (response?.code === 200) {
          message.success("Vehicle updated successfully!");
          setFormData({
            vehicleDetails: "",
            makeAndModel: "",
            registrationNum: "",
            insuranceNumber: "",
            insuranceProvider: "",
            insuranceValidTill: null,
            lastValidityDate: null,
            serviceReminder: "",
            assignedDriver: "",
          });
          navigate("/login/vehicle-management");
        } else {
          message.error(response?.message || "Failed to update vehicle.");
        }
      } else {
        response = await dataService.addVehicle(payload);
        if (response?.code === 201) {
          message.success("Vehicle registered successfully!");
          navigate("/login/vehicle-management");
        } else {
          message.error(response?.message || "Failed to register vehicle.");
        }
      }
      setFormData({
        vehicleDetails: "",
        makeAndModel: "",
        registrationNum: "",
        insuranceNumber: "",
        insuranceProvider: "",
        insuranceValidTill: null,
        lastValidityDate: null,
        serviceReminder: "",
        assignedDriver: "",
      });
    } catch (error) {
      console.error("Vehicle submission error:", error);
      message.error("Something went wrong. Please try again.");
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      const drivers = await dataService.getDriver();

      setDriverList(drivers.data);
    };

    fetchData();
  }, []);
  const handleDriverChange = (driverId) => {
    const driver = driverList.find((d) => d._id === driverId);
  
    if (driver.isAssigned && driver.assignedVehicle) {
      setPendingDriver(driver); // Store temporarily
      setShowConfirmModal(true); // Show modal
    } else {
      setSelectedDriver(driverId); // Directly assign
      setFormData({ ...formData, assignedDriver: driverId }); // update formData
    }
  };
  
  const confirmReassign = () => {
    setSelectedDriver(pendingDriver._id);
    setFormData({ ...formData, assignedDriver: pendingDriver._id });
    setPendingDriver(null);
    setShowConfirmModal(false);
    message.success("Driver reassigned successfully");
  };
  
  const cancelReassign = () => {
    setPendingDriver(null);
    setShowConfirmModal(false);
  };

  return (
    <Card
  style={{
    maxWidth: 900,
    margin: "40px auto",
    padding: 20,
    borderRadius: 10,
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  }}
>
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <motion.img
      src={icon}
      alt="Trip Icon"
      style={{
        width: "40px",
        height: "40px",
        marginRight: "10px",
        animation: "bounce 4s infinite",
      }}
    />
    <Title level={3} style={{ marginBottom: 20 }}>
      {editMode ? "Edit Vehicle" : "Vehicle Registration"}
    </Title>
  </motion.div>

  <Steps current={currentStep} style={{ marginBottom: 30 }}>
    {steps.map((step, index) => (
      <Step key={index} title={step.title} icon={step.icon} />
    ))}
  </Steps>

  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.5 }}
  >
    {currentStep === 0 && (
      <div>
        <Typography.Title level={4}>Vehicle Details</Typography.Title>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Select
              placeholder="Select Vehicle Type"
              value={formData.vehicleDetails}
              onChange={(value) =>
                setFormData({ ...formData, vehicleDetails: value })
              }
              style={{ width: "100%" }}
            >
              <Option value="2W">2W</Option>
              <Option value="4W">4W</Option>
            </Select>
          </Col>

          <Col xs={24} md={12}>
            <Input
              placeholder="Make & Model"
              value={formData.makeAndModel}
              onChange={(e) =>
                setFormData({ ...formData, makeAndModel: e.target.value })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <Input
              placeholder="Registration Number"
              value={formData.registrationNum}
              onChange={(e) =>
                setFormData({ ...formData, registrationNum: e.target.value })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <Input
              placeholder="Insurance Number"
              value={formData.insuranceNumber}
              onChange={(e) =>
                setFormData({ ...formData, insuranceNumber: e.target.value })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <Input
              placeholder="Insurance Provider"
              value={formData.insuranceProvider}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  insuranceProvider: e.target.value,
                })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Insurance Validity"
              value={formData.lastValidityDate}
              onChange={(date) =>
                setFormData({ ...formData, lastValidityDate: date })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Last Validity Date"
              value={formData.insuranceValidTill}
              onChange={(date) =>
                setFormData({ ...formData, insuranceValidTill: date })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <Input
              placeholder="Service Reminders"
              value={formData.serviceReminder}
              onChange={(e) =>
                setFormData({ ...formData, serviceReminder: e.target.value })
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => handleFileUpload(info, "insuranceImage")}
            >
              <Button icon={<UploadOutlined />}>Upload Insurance</Button>
            </Upload>
          </Col>

          <Col xs={24} md={12}>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => handleFileUpload(info, "vehicleImage")}
            >
              <Button icon={<UploadOutlined />}>Upload Vehicle Image</Button>
            </Upload>
          </Col>
        </Row>
      </div>
    )}

    {currentStep === 1 && (
      <div>
        <Typography.Title level={4}>Assign A Driver</Typography.Title>
        <Form.Item
          name="driverId"
          rules={[{ required: true, message: "Please select a driver!" }]}
        >
          <Select
            placeholder="Select a driver"
            value={formData.assignedDriver}
            onChange={(value) => handleDriverChange(value)}
            style={{ width: "100%", borderRadius: "8px" }}
            loading={driverList.length === 0}
          >
            {driverList.map((driver) => (
              <Select.Option key={driver._id} value={driver._id}>
                {driver.driverName} - {driver.driverPhoneNumber} -{" "}
                {driver.isAssigned && driver.assignedVehicle
                  ? `Assigned to ${driver.assignedVehicle} `
                  : "Not assigned"}
              </Select.Option>
            ))}
          </Select>

          <Modal
            title="Driver Already Assigned"
            visible={showConfirmModal}
            onOk={confirmReassign}
            onCancel={cancelReassign}
            okText="Yes, Reassign"
            cancelText="No"
          >
            <p>
              This driver is already assigned to vehicle{" "}
              <b>{pendingDriver?.assignedVehicle?.registrationNum}</b>.
            </p>
            <p>Do you want to reassign this driver to your vehicle?</p>
          </Modal>
        </Form.Item>
      </div>
    )}
  </motion.div>

  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      marginTop: 30,
      flex: 1,
      gap: "10px",
    }}
  >
    <Button
      disabled={currentStep === 0}
      style={{ borderColor: colors.button, color: colors.button }}
      onClick={handleBack}
    >
      Previous
    </Button>

    {currentStep < steps.length - 1 ? (
      <Button
        type="primary"
        style={{ backgroundColor: colors.button }}
        onClick={handleNext}
      >
        Next
      </Button>
    ) : (
      <Button
        type="primary"
        style={{ backgroundColor: colors.button }}
        onClick={handleSubmit}
      >
        Submit
      </Button>
    )}
  </div>
</Card>

  );
};

export default TransportDetails;
